import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import { siteAPI } from '../lib/api';
import { 
  LayoutDashboard, 
  ShoppingBag, 
  Package, 
  BookOpen, 
  HelpCircle, 
  User, 
  Settings,
  LogOut,
  ShoppingCart,
  Users,
  FileText,
  BarChart3,
  Bell,
  Webhook,
  ChevronDown,
  ChevronRight,
  Search,
  Sparkles,
  Mail,
  CreditCard,
  Crown,
  ExternalLink
} from 'lucide-react';
import { cn } from '../lib/utils';
import NotificationBell from './NotificationBell';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './ui/tooltip';
import AIChatWidget from './chat/AIChatWidget';
import TrialBanner from './dashboard/TrialBanner';
import '../styles/dashboard.css';

const BACKEND_URL = '';

const getLogoUrl = (settings) => {
  if (!settings?.logo_url) return null;
  if (settings.logo_url.startsWith('http')) return settings.logo_url;
  return `${BACKEND_URL}${settings.logo_url}`;
};

export default function Layout({ children }) {
  const { user, logout, isAdmin } = useAuth();
  const { cart } = useCart();
  const location = useLocation();
  const [siteSettings, setSiteSettings] = useState(null);
  const [expandedSections, setExpandedSections] = useState({});

  useEffect(() => {
    loadSiteSettings();
  }, []);

  const loadSiteSettings = async () => {
    try {
      const res = await siteAPI.getSiteSettings();
      setSiteSettings(res.data);
    } catch (error) {
      console.error('Failed to load site settings:', error);
    }
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const clientNav = [
    { name: 'Dashboard', icon: LayoutDashboard, path: '/dashboard' },
    { name: 'Browse Services', icon: ShoppingBag, path: '/dashboard/services' },
    { name: 'My Services', icon: Package, path: '/my-services' },
    { name: 'Orders', icon: CreditCard, path: '/orders' },
    { name: 'Knowledge Base', icon: BookOpen, path: '/knowledge-base' },
    { name: 'Support', icon: HelpCircle, path: '/support' },
  ];

  const adminNav = [
    { name: 'Dashboard', icon: BarChart3, path: '/admin' },
    { name: 'Clients', icon: Users, path: '/admin/clients' },
    { name: 'Services', icon: ShoppingBag, path: '/admin/services' },
    { name: 'Orders', icon: Package, path: '/admin/orders' },
    { name: 'Team', icon: Users, path: '/admin/team' },
    { name: 'Knowledge Base', icon: FileText, path: '/admin/knowledge-base' },
    { name: 'Blog', icon: BookOpen, path: '/admin/blog' },
    { name: 'Notifications', icon: Bell, path: '/admin/notifications' },
    { name: 'Integrations', icon: Webhook, path: '/admin/integrations' },
    { name: 'AI Assistant', icon: Sparkles, path: '/admin/ai-settings' },
    { name: 'Email Settings', icon: Mail, path: '/admin/email-settings' },
    { 
      name: 'Settings', 
      icon: Settings, 
      hasChildren: true,
      children: [
        { name: 'Site Settings', path: '/admin/settings' },
        { name: 'Portal Settings', path: '/admin/portal-settings' },
      ]
    },
    { name: 'Master Admin', icon: Crown, path: '/master', divider: true },
  ];

  const isAdminContext = isAdmin && (location.pathname.startsWith('/admin') || location.pathname === '/profile');
  const navigation = isAdminContext ? adminNav : clientNav;

  return (
    <TooltipProvider>
      <div className="min-h-screen bg-white">
        {/* Sidebar - Chatbase Style */}
        <aside className="fixed inset-y-0 left-0 z-50 w-60 bg-white border-r border-[#EBEBEB]">
          <div className="flex flex-col h-full">
            {/* Header with Logo */}
            <div className="h-14 flex items-center px-4 border-b border-[#EBEBEB]">
              <Link to={isAdmin ? '/admin' : '/dashboard'} className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg flex-shrink-0 overflow-hidden">
                  {siteSettings?.logo_url ? (
                    <img 
                      src={getLogoUrl(siteSettings)} 
                      alt={siteSettings?.site_name || 'Logo'}
                      className="w-full h-full object-contain"
                    />
                  ) : (
                    <img src="/edrano-logo-dark.png" alt="Edrano" className="w-full h-full object-contain" />
                  )}
                </div>
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <h1 className="font-semibold text-sm text-[#0A0A0A] truncate">
                      {siteSettings?.site_name || 'Edrano'}
                    </h1>
                    <span className="text-[10px] px-1.5 py-0.5 bg-[#F5F5F5] text-[#525252] rounded font-medium">
                      {isAdminContext ? 'Admin' : 'Portal'}
                    </span>
                  </div>
                </div>
              </Link>
            </div>

            {/* Navigation */}
            <nav className="flex-1 py-3 px-3 space-y-1 overflow-y-auto">
              {navigation.map((item) => {
                const Icon = item.icon;
                const isActive = location.pathname === item.path || 
                  (item.path && item.path !== '/admin' && location.pathname.startsWith(item.path));
                const hasChildren = item.hasChildren && item.children;
                const isExpanded = expandedSections[item.name];
                const isChildActive = hasChildren && item.children.some(child => location.pathname === child.path);
                
                if (hasChildren) {
                  return (
                    <div key={item.name}>
                      <button
                        onClick={() => toggleSection(item.name)}
                        className={cn(
                          'w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all text-sm',
                          isChildActive
                            ? 'bg-black text-white'
                            : 'text-[#525252] hover:bg-[#F5F5F5] hover:text-[#0A0A0A]'
                        )}
                        data-testid={`nav-${item.name.toLowerCase().replace(' ', '-')}`}
                      >
                        <Icon className="w-5 h-5" />
                        <span className="flex-1 text-left font-medium">{item.name}</span>
                        <ChevronDown className={cn(
                          "w-4 h-4 transition-transform",
                          isExpanded && "rotate-180"
                        )} />
                      </button>
                      {isExpanded && (
                        <div className="mt-1 ml-8 space-y-1">
                          {item.children.map((child) => (
                            <Link
                              key={child.path}
                              to={child.path}
                              className={cn(
                                'block px-3 py-2 rounded-lg text-sm transition-all',
                                location.pathname === child.path
                                  ? 'bg-[#F5F5F5] text-[#0A0A0A] font-medium'
                                  : 'text-[#525252] hover:bg-[#F5F5F5] hover:text-[#0A0A0A]'
                              )}
                            >
                              {child.name}
                            </Link>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                }
                
                return (
                  <React.Fragment key={item.path}>
                    {item.divider && <div className="my-2 border-t border-slate-200" />}
                    <Link
                      to={item.path}
                      data-testid={`nav-${item.name.toLowerCase().replace(' ', '-')}`}
                      className={cn(
                        'flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all text-sm',
                        isActive
                          ? 'bg-black text-white'
                        : 'text-[#525252] hover:bg-[#F5F5F5] hover:text-[#0A0A0A]'
                    )}
                  >
                    <Icon className="w-5 h-5" />
                    <span className="font-medium">{item.name}</span>
                  </Link>
                  </React.Fragment>
                );
              })}
            </nav>

            {/* Bottom Section - Credits/User */}
            <div className="border-t border-[#EBEBEB]">
              {/* User Profile Section */}
              <div className="p-3">
                <Link
                  to="/profile"
                  className="flex items-center gap-3 p-2 rounded-lg hover:bg-[#F5F5F5] transition-all"
                  data-testid="profile-button"
                >
                  <div className="w-8 h-8 bg-[#F5F5F5] rounded-full flex items-center justify-center flex-shrink-0">
                    <User className="w-4 h-4 text-[#525252]" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-[#0A0A0A] truncate">{user?.full_name}</p>
                    <p className="text-xs text-[#A3A3A3] truncate">{user?.email}</p>
                  </div>
                </Link>
              </div>
              
              {/* Logout Button */}
              <div className="px-3 pb-3">
                <button
                  onClick={logout}
                  className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-sm font-medium text-[#DC2626] bg-[#FEE2E2] hover:bg-[#FECACA] transition-all"
                  data-testid="logout-button"
                >
                  <LogOut className="w-4 h-4" />
                  <span>Logout</span>
                </button>
              </div>
            </div>
          </div>
        </aside>

        {/* Main Content */}
        <main className="pl-60">
          {/* Header - Clean & Minimal */}
          <header className="sticky top-0 z-40 h-14 bg-white border-b border-[#EBEBEB]">
            <div className="flex items-center justify-between h-full px-6">
              {/* Page Title */}
              <h2 className="font-semibold text-lg text-[#0A0A0A]">
                {navigation.find(item => item.path === location.pathname)?.name || 
                 navigation.flatMap(item => item.children || []).find(child => child.path === location.pathname)?.name ||
                 'Dashboard'}
              </h2>
              
              {/* Right Side Actions */}
              <div className="flex items-center gap-4">
                {/* Search */}
                <div className="relative hidden md:block">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#A3A3A3]" />
                  <input 
                    type="text"
                    placeholder="Search..."
                    className="w-64 h-9 pl-10 pr-4 text-sm bg-[#F5F5F5] border-0 rounded-lg focus:outline-none focus:ring-2 focus:ring-black/5"
                  />
                </div>
                
                <NotificationBell />
                
                {isAdmin && (
                  <a
                    href={`/app/sociably`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 px-3 py-1.5 rounded-lg border border-slate-200 text-sm font-medium text-slate-600 hover:bg-slate-50 transition-all"
                    data-testid="view-portal-button"
                  >
                    <ExternalLink className="w-3.5 h-3.5" />
                    <span>View Portal</span>
                  </a>
                )}
                
                {!isAdmin && (
                  <Link
                    to="/cart"
                    className="relative flex items-center gap-2 px-4 py-2 rounded-lg bg-black text-white text-sm font-medium hover:bg-[#262626] transition-all"
                    data-testid="cart-button"
                  >
                    <ShoppingCart className="w-4 h-4" />
                    <span>Cart</span>
                    {cart.length > 0 && (
                      <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs font-bold rounded-full flex items-center justify-center">
                        {cart.length}
                      </span>
                    )}
                  </Link>
                )}
              </div>
            </div>
          </header>

          {/* Page Content */}
          <div className="p-6">
            {/* Trial Banner - shows for admin users on trial */}
            {isAdmin && <TrialBanner />}
            
            {children}
          </div>
        </main>
        
        {/* AI Chat Widget */}
        <AIChatWidget />
      </div>
    </TooltipProvider>
  );
}
